// @flow
import React, {Component} from "react";
import InfiniteScroll from "react-infinite-scroller";
import CarSelectorContainer from "../../container/carSelector/CarSelectionBarContainer";
import type {translate} from "../../i18next";
import type Category from "../../models/Category";
import type {Product} from "../../models/Product";
import type {ConfigReducerState} from "../../reducers/configReducer";
import "./Products.scss";
import ShowCaseContainer from "../../container/gridPages/ShowCaseContainer";
import EventEmitter from "../../helper/eventEmitter";
import GridElementContainer from "../../container/gridPages/GridElementContainer";
import {resetPageMeta, setPageMeta} from "../../helper/pageMeta";
import history from "../../history";
import {Link} from "react-router-dom";

type ProductsProps = {
	location: any,
	products: Product[],
	getNextProducts: () => void,
	loadProducts: () => void,
	toStartPage: () => void,
	setCategory: Category => void,
	config: ConfigReducerState,
	t: translate,
	currentPage: number,
	totalPages: number,
	category: string,
	categoryLabel: string,
	totalItems: number,
	isLoading: boolean,
	hasMore: boolean,
	resetVehicle: () => void,
	selectedVehicle: string,
	setVehicle: (string, string) => void,
	markers: [],
	setMarker: () => void
}

class Products extends Component<ProductsProps> {
	constructor(props: ProductsProps) {
		super(props);
		props.setCategory(props.category);
		this.state = {
			category: props.category
		};
	}

	componentDidMount() {
		this.props.setMarker(this.props.markers);
		// Force the first page load for this category (see loadProducts in the
		// container) instead of the infinite-scroll path, which is suppressed
		// while a previous request is still loading.
		this.props.loadProducts();
		new EventEmitter().emitEvent("HPM_PAGE_LOADED");
		this.updatePageTitle();
	}

	componentDidUpdate() {
		if (this.state.category !== this.props.category) {
			this.props.setMarker(this.props.markers);
			this.props.setCategory(this.props.category);
			// Switching category must always start a fresh request, even if the
			// previous category is still loading, so the in-flight (now stale)
			// response cannot fill this category's grid (#44874).
			this.props.loadProducts();
			this.setState({...this.state, category: this.props.category});
			new EventEmitter().emitEvent("HPM_PAGE_LOADED");
			this.updatePageTitle();
		}
	}

	componentWillUnmount() {
		resetPageMeta();
	}

	// route param may decode to the literal string "undefined" on the
	// category-less /products route — no title override in that case
	updatePageTitle = () => {
		if (this.props.category && this.props.category !== "undefined") {
			const label = this.props.categoryLabel || this.props.category;
			const dealerName = this.props.config && this.props.config.name;
			setPageMeta({
				title: this.props.category,
				description: dealerName ? `${label} – ${dealerName}` : label,
			});
		}
	};

	loadMoreProducts = () => {
		if (this.props.hasMore) {
			this.props.getNextProducts();
		}
	};

	render() {
		const allProductsLoaded = this.props.products.length >= this.props.totalItems;
		return <div className="hpm-products">
			<div className="hpm-carContainer">
				<CarSelectorContainer resetVehicle={this.props.resetVehicle} setVehicle={this.props.setVehicle}/>
			</div>
			<ShowCaseContainer showTileFallback={true} selectedCategoryKey={this.props.category}/>
			<div className="hpm-categoryContainer">
				<Link
					className="hpm-categoryTitle"
					to={history.getHomeUrl()}
					onClick={e => {
						// real href for crawlers, existing start page logic for users
						e.preventDefault();
						this.props.toStartPage();
					}}
				>
					{this.props.categoryLabel}
				</Link>
				<div className="hpm-gridList">
					<InfiniteScroll
						pageStart={0}
						initialLoad={false}
						loadMore={this.props.getNextProducts}
						hasMore={false}
						loader={<React.Fragment/>}
						threshold={250}
					>
						{
							this.props.products.map(product => {
								return <GridElementContainer	key={product.id} product={product}/>;
							})
						}
					</InfiniteScroll>
					{allProductsLoaded && (
						<div className="total-items">
							{`${this.props.totalItems} ${this.props.t("category.allProductsLoaded")}`}
						</div>
					)}
					{!allProductsLoaded && (
						<button type={"button"} className={"show-more-products"} onClick={this.loadMoreProducts}>
							{this.props.t("category.showAll")}
						</button>
					)}
				</div>
			</div>
		</div>;
	};
}

export default Products;
